import { useEffect, useId, useState } from 'react'
import type { FormEvent } from 'react'
import { Alert } from '../Alert'
import { Button } from '../Button'
import { Card } from '../Card'
import { FormField } from '../FormField'
import { Input } from '../Input'
import { OtpInput } from '../OtpInput'
import { cn } from '../cn'
import { describedBy } from '../describedBy'
import { AUTH_ACTIONS, actionDataAttrs, recipeDataAttrs } from './markers'

export type AuthSurfaceProps = {
  step: 'email' | 'otp'
  email: string
  otp: string
  error?: string
  loading?: boolean
  title?: string
  onEmailChange: (v: string) => void
  onOtpChange: (v: string) => void
  onRequestOtp: () => void
  onVerifyOtp: (otp?: string) => void
  /** Optional resend handler. When set, shows a low-emphasis Resend control on the OTP step. */
  onResend?: () => void
  /** Cooldown length in seconds after entering the OTP step (and after each resend). Default 30. */
  resendCooldownSeconds?: number
  /** Optional handler to leave the OTP step and pick a different email. */
  onChangeEmail?: () => void
  /**
   * Submit automatically when the last digit is entered. Off by default: several
   * consumers already auto-submit from onOtpChange, and doing both double-submits
   * the code (the second verify then fails against a burned OTP).
   */
  autoSubmitOnComplete?: boolean
  className?: string
}

/**
 * Auth-1 — single login recipe for all product apps.
 * Uses shared FormField, Input, OtpInput, Button, Card, Alert only.
 */
export function AuthSurface({
  step,
  email,
  otp,
  error,
  loading = false,
  title = 'Sign in',
  onEmailChange,
  onOtpChange,
  onRequestOtp,
  onVerifyOtp,
  onResend,
  resendCooldownSeconds = 30,
  onChangeEmail,
  autoSubmitOnComplete = false,
  className = '',
}: AuthSurfaceProps) {
  const errorId = useId()
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0)

  useEffect(() => {
    if (step !== 'otp' || !onResend) {
      setResendSecondsLeft(0)
      return
    }

    const cooldown = Math.max(0, Math.floor(resendCooldownSeconds))
    setResendSecondsLeft(cooldown)
    if (cooldown === 0) return

    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000)
      const remaining = Math.max(0, cooldown - elapsed)
      setResendSecondsLeft(remaining)
      if (remaining === 0) {
        window.clearInterval(timer)
      }
    }, 250)

    return () => window.clearInterval(timer)
  }, [step, onResend, resendCooldownSeconds])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (step === 'email') onRequestOtp()
    else onVerifyOtp()
  }

  const handleResend = () => {
    if (!onResend || resendSecondsLeft > 0 || loading) return
    onResend()
    const cooldown = Math.max(0, Math.floor(resendCooldownSeconds))
    setResendSecondsLeft(cooldown)
  }

  const fieldDescribedBy = describedBy(error ? errorId : undefined)
  const otpInvalid = error ? true : undefined

  return (
    <div
      {...recipeDataAttrs('Auth-1')}
      className={cn('mx-auto w-full max-w-md', className)}
      data-auth-step={step}
    >
      <Card className="p-6">
        <h1 className="m-0 mb-4 font-display text-h2 text-strong">{title}</h1>
        {error ? (
          <Alert id={errorId} variant="error" className="mb-4">
            {error}
          </Alert>
        ) : null}
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          {step === 'email' ? (
            <>
              <FormField label="Work email" required>
                {({ id, describedBy: fieldHintIds, invalid }) => (
                  <Input
                    id={id}
                    type="email"
                    autoComplete="email"
                    autoFocus
                    aria-label="Email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => onEmailChange(e.target.value)}
                    aria-describedby={describedBy(fieldHintIds, fieldDescribedBy)}
                    aria-invalid={invalid || (error ? true : undefined)}
                    disabled={loading}
                    required
                  />
                )}
              </FormField>
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                {...actionDataAttrs(AUTH_ACTIONS.requestOtp)}
              >
                Send code
              </Button>
            </>
          ) : (
            <>
              <p className="m-0 text-sm text-ink-secondary">
                Enter the code sent to <strong>{email}</strong>
              </p>
              {/* a11y: a form label's htmlFor must point at a labelable control;
                  pointing it at this wrapper div is invalid and leaves the visible
                  label programmatically unassociated. The OtpInput group carries
                  its own accessible name (aria-label="One-time password"), so the
                  visible text here is a plain heading-style element, not htmlFor. */}
              <div>
                <p className="block text-sm font-medium text-ink-secondary mb-1">
                  One-time code<span aria-hidden="true" className="text-err ml-0.5">*</span>
                </p>
                <OtpInput
                  value={otp}
                  onChange={onOtpChange}
                  disabled={loading}
                  autoFocus
                  onComplete={autoSubmitOnComplete ? onVerifyOtp : undefined}
                  aria-describedby={fieldDescribedBy}
                  aria-invalid={otpInvalid}
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                {...actionDataAttrs(AUTH_ACTIONS.verifyOtp)}
              >
                Verify
              </Button>
              {(onResend || onChangeEmail) ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {onResend ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={loading || resendSecondsLeft > 0}
                      onClick={handleResend}
                    >
                      {resendSecondsLeft > 0
                        ? `Resend code (${resendSecondsLeft}s)`
                        : 'Resend code'}
                    </Button>
                  ) : <span />}
                  {onChangeEmail ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={loading}
                      onClick={onChangeEmail}
                    >
                      Use a different email
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </form>
      </Card>
    </div>
  )
}
